
import { useState } from 'react';

export default function CTASection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6dghc6qd308q1a0a1s0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1B3A6B] mb-6">
              Schedule Your Service Today
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Experience the Voltis difference. Fill out the form and one of our friendly team members will contact you shortly to schedule your service at a time that works for you.
            </p>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#1B3A6B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-fill text-2xl text-[#B22234]"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-[#1B3A6B] mb-1">Call Us Anytime</h4>
                  <a href="tel:+18005551234" className="text-[#B22234] font-semibold hover:text-[#8B1A29] transition-colors">
                    (800) 555-1234
                  </a>
                  <p className="text-sm text-gray-600 mt-1">Available 24/7 for emergencies</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#1B3A6B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-fill text-2xl text-[#1B3A6B]"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-[#1B3A6B] mb-1">Email Us</h4>
                  <a href="mailto:info@voltisflorida.com" className="text-[#B22234] font-semibold hover:text-[#8B1A29] transition-colors">
                    info@voltisflorida.com
                  </a>
                  <p className="text-sm text-gray-600 mt-1">We'll respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#1B3A6B]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-fill text-2xl text-[#1B3A6B]"></i>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-[#1B3A6B] mb-1">Business Hours</h4>
                  <p className="text-sm text-gray-600">Monday to Friday: 7:00 AM to 8:00 PM</p>
                  <p className="text-sm text-gray-600">Saturday: 8:00 AM to 6:00 PM</p>
                  <p className="text-sm text-gray-600">Sunday: 9:00 AM to 5:00 PM</p>
                  <p className="text-sm text-[#B22234] font-semibold mt-1">Emergency Service: 24/7</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#F8F9FC] rounded-2xl p-8 shadow-lg border border-[#1B3A6B]/10">
            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#1B3A6B] mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1B3A6B] focus:border-transparent text-sm"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#1B3A6B] mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1B3A6B] focus:border-transparent text-sm"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-[#1B3A6B] mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1B3A6B] focus:border-transparent text-sm"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-[#1B3A6B] mb-2">
                    Service Needed *
                  </label>
                  <select
                    id="service"
                    name="service"
                    required
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1B3A6B] focus:border-transparent text-sm"
                  >
                    <option value="">Select a service</option>
                    <option value="Air Conditioning">Air Conditioning</option>
                    <option value="Heating">Heating</option>
                    <option value="Plumbing">Plumbing</option>
                    <option value="Water Quality">Water Quality</option>
                    <option value="Electrical">Electrical</option>
                    <option value="Maintenance Plan">Maintenance Plan</option>
                    <option value="Emergency Service">Emergency Service</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-[#1B3A6B] mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1B3A6B] focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about your service needs..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#B22234] text-white px-6 py-4 rounded-lg text-sm font-semibold hover:bg-[#8B1A29] transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Submitting...' : 'Schedule Service'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                    <i className="ri-check-line mr-2"></i>
                    Thank you! We'll contact you shortly to schedule your service.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    <i className="ri-error-warning-line mr-2"></i>
                    Something went wrong. Please call us at (800) 555-1234.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
